import React from 'react';
import styled from 'styled-components';

import { makeNamespacedComponent } from '@/ui/utils';
import { Button } from '@/ui/Button';
import { Skeleton } from '@/ui/Skeleton';
import { JourneyParticipant, JourneyParticipantProps } from '@/features/journey/admin/atoms/JourneyParticipant';

const RootStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CounterStyled = styled.div`
  display: flex;
  justify-content: flex-end;
  ${({ theme }) => theme.typography.body3};
  color: ${({ theme }) => theme.colors.neutral.onBackground};
`;

const ListStyled = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.neutral.outline1};
  border-radius: 4px;
  display: flex;
  flex-direction: column;

  & > :not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutral.outline1};
  }
`;

const LoadMoreStyled = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 24px;
`;

type ParticipantsProps = {
  participants: JourneyParticipantProps[];
  total: number;
  loadingMoreItems?: boolean;
  onLoadMoreItems?: () => void;
};

const ListLoading = (): JSX.Element => (
  <>
    <JourneyParticipant.LoadingState />
    <JourneyParticipant.LoadingState />
    <JourneyParticipant.LoadingState />
  </>
);

const _JourneyParticipants = ({ participants, total, onLoadMoreItems, loadingMoreItems }: ParticipantsProps): JSX.Element => {
  const canLoadMore = participants.length < total;
  return (
    <RootStyled>
      <CounterStyled>
        {total} {`learner${total !== 1 ? 's' : ''}`}
      </CounterStyled>
      <ListStyled>
        {participants.map((participantProps) => (
          <JourneyParticipant {...participantProps} key={participantProps.email} />
        ))}
        {loadingMoreItems && <ListLoading />}
      </ListStyled>
      {canLoadMore && (
        <LoadMoreStyled>
          <Button variant="secondary" onClick={onLoadMoreItems} disabled={loadingMoreItems}>
            Show more
          </Button>
        </LoadMoreStyled>
      )}
    </RootStyled>
  );
};

const LoadingState = (): JSX.Element => (
  <RootStyled>
    <CounterStyled>
      <Skeleton height={20} width={100} />
    </CounterStyled>

    <ListStyled>
      <ListLoading />
    </ListStyled>
  </RootStyled>
);

const JourneyParticipants = makeNamespacedComponent(_JourneyParticipants, {
  LoadingState,
});

export { JourneyParticipants };
